import {
  AnyReferenceCriteriaModel,
  CabinetCriteriaModel,
  CriteriaComparisonType,
  CriteriaModel,
  DateRangeType,
  DocumentDateRangeModel,
  FullTextSearchModel,
  OrderByModel,
  OrderByType,
  SearchRequestModel
} from '@/services/GeneratedApi';
import { SearchCriteriaModel, SearchPeriod } from '@/types';
import { ColumnFilterType } from '@/types/enums/ColumnFilterType';

export const SearchCriteriaUtils = {
  mapColumnFilterToCriteriaComparison(filterType: ColumnFilterType | null): CriteriaComparisonType {
    switch (filterType) {
      case ColumnFilterType.Equals:
        return CriteriaComparisonType.Equals;
      case ColumnFilterType.DoesNotEqual:
        return CriteriaComparisonType.DoesNotEqual;
      case ColumnFilterType.LessThan:
        return CriteriaComparisonType.LessThan;
      case ColumnFilterType.LessThanOrEqualTo:
        return CriteriaComparisonType.LessThanOrEqualTo;
      case ColumnFilterType.GreaterThan:
        return CriteriaComparisonType.GreaterThan;
      case ColumnFilterType.GreaterThanOrEqualTo:
        return CriteriaComparisonType.GreaterThanOrEqualTo;
      case ColumnFilterType.StartsWith:
        return CriteriaComparisonType.StartsWith;
      case ColumnFilterType.EndsWith:
        return CriteriaComparisonType.EndsWith;
      case ColumnFilterType.Contains:
      case ColumnFilterType.Like:
        return CriteriaComparisonType.Contains;
      case ColumnFilterType.DoesNotStartWith:
        return CriteriaComparisonType.DoesNotStartWith;
      case ColumnFilterType.DoesNotContain:
      case ColumnFilterType.NotLike:
        return CriteriaComparisonType.DoesNotContain;
      case ColumnFilterType.DoesNotEndWith:
        return CriteriaComparisonType.DoesNotEndWith;
      default:
        return CriteriaComparisonType.StartsWith;
    }
  },

  createStandardCriteria(reference: { fieldKey: string; value: string }): CriteriaModel {
    return {
      field: reference.fieldKey,
      value: reference.value,
      comparisonType: CriteriaComparisonType.StartsWith
    };
  },

  createAdvancedCriteria(reference: { fieldKey: string | null; value: string; filterType: ColumnFilterType | null }): CriteriaModel {
    return {
      field: reference.fieldKey ?? '',
      value: reference.value,
      comparisonType: this.mapColumnFilterToCriteriaComparison(reference.filterType)
    };
  },

  mapSearchPeriodToDateRangeType(searchPeriod: SearchPeriod): DateRangeType {
    switch (searchPeriod) {
      case SearchPeriod.LastMonth:
        return DateRangeType.LastMonth;
      case SearchPeriod.LastThreeMonths:
        return DateRangeType.LastThreeMonths;
      case SearchPeriod.LastYear:
        return DateRangeType.LastYear;
      case SearchPeriod.Custom:
        return DateRangeType.Range;
      default:
        return DateRangeType.All;
    }
  },

  createCabinetCriteria(id: string, documentTypes: number[]): CabinetCriteriaModel {
    return {
      id,
      documentTypes: documentTypes?.length > 0 ? documentTypes : null
    };
  },

  createOrderByModel(orderBy: { field: string; isSortingAscending: boolean }): OrderByModel {
    return {
      field: orderBy?.field,
      orderByType: orderBy?.isSortingAscending ? OrderByType.Asc : OrderByType.Desc
    };
  },

  createAnyReferenceCriteria(anyReference: { value: string; filterType: ColumnFilterType | null; includeFullText?: boolean } | null): AnyReferenceCriteriaModel | undefined {
    if (!anyReference?.value) {
      return undefined;
    }

    return {
      value: anyReference.value,
      comparisonType: anyReference.filterType ? this.mapColumnFilterToCriteriaComparison(anyReference.filterType) : CriteriaComparisonType.StartsWith,
      includeFullText: anyReference.includeFullText
    };
  },

  createDateRangeCriteria(dateRange: { searchPeriod: SearchPeriod; from: Date; to: Date } | null): DocumentDateRangeModel {
    if (!dateRange) {
      return {
        rangeType: DateRangeType.All,
        toDate: null,
        fromDate: null
      };
    }

    const rangeType = this.mapSearchPeriodToDateRangeType(dateRange?.searchPeriod);

    return {
      rangeType,
      fromDate: rangeType === DateRangeType.Range ? this.formatDateForApi(dateRange.from) : null,
      toDate: rangeType === DateRangeType.Range ? this.formatDateForApi(dateRange.to) : null
    };
  },

  mapCriteria(searchCriteria: SearchCriteriaModel): CriteriaModel[] {
    if (!searchCriteria?.isAdvancedSearch && searchCriteria?.standardSearch) {
      return searchCriteria?.standardSearch?.map((reference) => this.createStandardCriteria(reference));
    }

    if (searchCriteria?.advancedSearch) {
      return searchCriteria?.advancedSearch?.map((reference) => this.createAdvancedCriteria(reference));
    }

    return [];
  },

  mapCabinets(cabinets: string[], documentTypes: number[]): CabinetCriteriaModel[] {
    return cabinets.map((id) => this.createCabinetCriteria(id, documentTypes));
  },

  mapToSearchRequest(searchCriteria: SearchCriteriaModel): SearchRequestModel {
    return {
      cabinets: this.mapCabinets(searchCriteria.cabinets, searchCriteria?.documentTypes),
      orderBy: this.createOrderByModel(searchCriteria?.orderBy),
      anyReferenceCriteria: this.createAnyReferenceCriteria(searchCriteria?.anyReference),
      documentDateRange: this.createDateRangeCriteria(searchCriteria?.dateRange),
      criteria: this.mapCriteria(searchCriteria),
      fullTextSearchModel: { isFullTextSearch: searchCriteria.isFullTextSearch, fullTextSearchValue: searchCriteria.fullTextSearchValue } as FullTextSearchModel
    };
  },

  formatDateForApi(date: Date) {
    // Create a new Date object if it isn't already one
    const d = new Date(date);

    // Format as YYYY-MM-DD
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');

    return `${year}-${month}-${day}`; // Will output "2024-12-18"
  }
};
